import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileUp, GanttChartSquare, Loader2, Sparkles } from "lucide-react";
import api from "@/lib/api";

export const ProcessAiBuilder = ({ onCreated }) => {
  const nav = useNavigate();
  const [mode, setMode] = useState(null); // "doc" | "plan"
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!mode) return;
    api.get("/projects").then(({ data }) => {
      const list = (data || []).filter((p) => !p.archived);
      setProjects(list);
      if (list.length === 1) setProjectId(list[0].id);
    }).catch(() => setProjects([]));
  }, [mode]);

  const closeDialog = () => { setMode(null); setFile(null); setName(""); setProjectId(""); };

  const build = async () => {
    if (!projectId) { toast.error("Pick a project to attach the process to"); return; }
    if (mode === "doc" && !file) { toast.error("Choose a document first"); return; }
    setBusy(true);
    try {
      let data;
      if (mode === "doc") {
        const fd = new FormData();
        fd.append("project_id", projectId);
        fd.append("name", name);
        fd.append("file", file);
        ({ data } = await api.post("/processes/ai-from-doc", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }));
      } else {
        ({ data } = await api.post("/processes/ai-from-plan", { project_id: projectId, name }, { timeout: 120000 }));
      }
      toast.success(`"${data.name}" mapped with ${data.steps.length} steps — opening the diagram`);
      onCreated?.(data);
      closeDialog();
      nav(`/projects/${data.project_id}?tab=process`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Maestro couldn't build the process — please try again");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" className="gap-2" onClick={() => setMode("doc")} data-testid="process-from-doc-button">
        <FileUp className="h-4 w-4" /> Build from document
      </Button>
      <Button variant="outline" className="gap-2" onClick={() => setMode("plan")} data-testid="process-from-plan-button">
        <GanttChartSquare className="h-4 w-4" /> Build from project plan
      </Button>

      <Dialog open={!!mode} onOpenChange={(v) => !v && !busy && closeDialog()}>
        <DialogContent className="sm:max-w-md" data-testid="process-ai-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> {mode === "doc" ? "Build a process from a document" : "Build a process from a project plan"}</DialogTitle>
            <DialogDescription>
              {mode === "doc"
                ? "Upload a roadmap or overview document (PDF, DOCX, TXT, MD) and Maestro will map it into a Visio-style process diagram."
                : "Maestro reads the project's phases, tasks and milestones and maps them into a Visio-style process diagram."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Attach to project</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger className="mt-1.5" data-testid="process-ai-project-select"><SelectValue placeholder="Choose a project…" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {mode === "doc" && (
              <div>
                <Label>Document</Label>
                <Input type="file" accept=".pdf,.docx,.txt,.md" className="mt-1.5 cursor-pointer"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="process-ai-file-input" />
              </div>
            )}
            <div>
              <Label>Process name <span className="text-muted-foreground font-normal">(optional — Maestro will name it)</span></Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Customer onboarding" className="mt-1.5" data-testid="process-ai-name-input" />
            </div>
            <Button className="w-full gap-2" onClick={build} disabled={busy} data-testid="process-ai-build-button">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Maestro is mapping the process…</> : <><Sparkles className="h-4 w-4" /> Build the diagram</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProcessAiBuilder;
